import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IFilter,
  IPopulate,
  IQueryParam,
  IServicioContratado,
} from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Subscription } from 'rxjs';
import {
  IRegExpSearch,
  IFiltroTabla,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { ServiciosContratadosService } from '../servicios-contratados.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';

@Component({
  selector: 'app-servicios',
  templateUrl: './servicios.component.html',
  styleUrl: './servicios.component.scss',
  standalone: false,
})
export class ServiciosComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  private dialogService = inject(DialogService);
  private service = inject(ServiciosContratadosService);

  //// Tabla nueva
  public name = 'ServiciosComponent';
  public datos: IServicioContratado[] = [];
  public servicios$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<IServicioContratado>[];
  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso(
      'Administración - Crear servicios ofrecidos',
    ),
    tooltip: 'Crear servicio',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };
  public search: IRegExpSearch = {
    fields: ['nombre'],
  };
  public filtros: IFiltroTabla[] = [];

  public filter: IFilter<IServicioContratado> = {};
  public populate: IPopulate = {};
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
  };

  public async listar(): Promise<void> {
    this.servicios$?.unsubscribe();
    this.servicios$ = this.lista2
      .serviciosContratados(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.datos = data.datos;
      });
  }

  // Acciones
  public async crear() {
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar(dato: IServicioContratado) {
    this.navigator.navigate(['editar', dato._id], {
      relativeTo: this.route,
    });
  }

  public async eliminar(dato: IServicioContratado) {
    const res = await this.dialogService.confirm(
      'Eliminar servicio',
      `¿Querés eliminar el servicio ${dato.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(dato._id!);
      this.helper.notifSuccess('Servicio eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IServicioContratado>[] = [
      // Identificacion
      {
        header: {
          label: 'Nombre',
          sortable: true,
          sort: 'nombre',
        },
        row: {
          // Servicio global: con su ícono y el texto (antes sólo el ícono)
          html: (dato) => {
            if (!dato.nombre && !dato.global) return '';
            let html = `<div class="tabla-celda-col">`;
            html += dato.nombre
              ? `<span class="tabla-principal">${escaparHtml(dato.nombre)}</span>`
              : `<span class="no-info">Sin nombre</span>`;
            if (dato.global) {
              html += `<div class="tabla-linea"><span class="material-symbols-outlined">public</span><span>Global</span></div>`;
            }
            return html + `</div>`;
          },
          noData: 'Sin nombre',
        },
      },
      // Costo
      {
        header: {
          label: 'Costo',
          sortable: true,
          sort: 'costo',
        },
        row: {
          // Importe con $ y separador de miles; centavos sólo si los hay
          parse: (dato) =>
            dato.costo == null
              ? undefined
              : `$ ${Number(dato.costo).toLocaleString('es-AR', {
                  minimumFractionDigits: Number.isInteger(Number(dato.costo))
                    ? 0
                    : 2,
                  maximumFractionDigits: 2,
                })}`,
          noData: 'Sin costo',
        },
      },
      // Ícono
      {
        header: {
          label: 'Ícono',
        },
        row: {
          html: (dato) =>
            dato.icono
              ? `<img src="${escaparHtml(dato.icono)}" alt="Ícono" class="row-icono-img">`
              : '',
          noData: 'Sin ícono',
        },
      },
    ];

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: (dato) =>
              !this.helper.tienePermiso(
                'Administración - Editar servicios ofrecidos',
              ) ||
              (dato.global && !this.helper.esNivel0()),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: (dato) =>
              !this.helper.tienePermiso(
                'Administración - Eliminar servicios ofrecidos',
              ) ||
              (dato.global && !this.helper.esNivel0()),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.servicios$?.unsubscribe();
  }
}
